import React from "react";
import { Box, Grid, Typography, Stack, Skeleton } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DevicesIcon from "@mui/icons-material/Devices";
import PublicIcon from "@mui/icons-material/Public";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { boxStyle } from "../../../../theme";

const ICON_BOX_SX = {
  width: 36,
  height: 36,
  borderRadius: "10px",
  backgroundColor: "rgba(255, 255, 255, 0.06)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const Num = ({ loading, value }) =>
  loading
    ? <Skeleton variant="text" width={70} height={36} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
    : <>{value}</>;

const SummaryCards = ({ products, cardsSummary, severitySummary, loading }) => {
  const navigate = useNavigate();
  const dateFrom = dayjs().subtract(7, "days").utc(true).toISOString();
  const dateTo = dayjs().utc(true).toISOString();

  const goTo = (path) => {
    const lastWeekQuery = `dateFrom=${encodeURIComponent(dateFrom)}&dateTo=${dateTo}`;
    navigate(
      sessionStorage.getItem("type") === "reseller"
        ? `../myC/${path}?${lastWeekQuery}`
        : `../security/${path}?${lastWeekQuery}`
    );
  };

  return (
    <Grid container spacing={2}>
      {/* Critical */}
      <Grid size={{ xs: 12, md: products.lecs ? 3 : 6 }}>
        <Box sx={{ ...boxStyle }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>Critical events</Typography>
            <Box sx={ICON_BOX_SX}>
              <ErrorOutlineIcon sx={{ fontSize: 18, color: "#ff4d4f" }} />
            </Box>
          </Stack>
          {products.lecs && (
            <Typography
              onClick={() => goTo("critical")}
              sx={{ color: "#fff", fontWeight: 800, fontSize: 22, lineHeight: 1.3, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              <Num loading={loading} value={<>{cardsSummary.critical.toLocaleString()}{" "}<Typography component="span" sx={{ fontSize: 12, color: "#555" }}>LECS</Typography></>} />
            </Typography>
          )}
          {products.agent && (
            <Typography
              onClick={() => goTo("critical")}
              sx={{ color: "#fff", fontWeight: 800, fontSize: 22, lineHeight: 1.3, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              <Num loading={loading} value={<>{cardsSummary.criticalAgent.toLocaleString()}{" "}<Typography component="span" sx={{ fontSize: 12, color: "#555" }}>Agent</Typography></>} />
            </Typography>
          )}
        </Box>
      </Grid>

      {/* High */}
      <Grid size={{ xs: 12, md: products.lecs ? 3 : 6 }}>
        <Box sx={{ ...boxStyle }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>High events</Typography>
            <Box sx={ICON_BOX_SX}>
              <WarningAmberIcon sx={{ fontSize: 18, color: "#FEA51D" }} />
            </Box>
          </Stack>
          {products.lecs && (
            <Typography
              onClick={() => goTo("high")}
              sx={{ color: "#fff", fontWeight: 800, fontSize: 22, lineHeight: 1.3, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              <Num loading={loading} value={<>{cardsSummary.high.toLocaleString()}{" "}<Typography component="span" sx={{ fontSize: 12, color: "#555" }}>LECS</Typography></>} />
            </Typography>
          )}
          {products.agent && (
            <Typography
              onClick={() => goTo("high")}
              sx={{ color: "#fff", fontWeight: 800, fontSize: 22, lineHeight: 1.3, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              <Num loading={loading} value={<>{cardsSummary.highAgent.toLocaleString()}{" "}<Typography component="span" sx={{ fontSize: 12, color: "#555" }}>Agent</Typography></>} />
            </Typography>
          )}
        </Box>
      </Grid>

      {products.lecs && (
        <>
          {/* Internal IPs */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ ...boxStyle }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>Internal → internal IPs</Typography>
                <Box sx={ICON_BOX_SX}>
                  <DevicesIcon sx={{ fontSize: 18, color: "#78d168" }} />
                </Box>
              </Stack>
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 22, lineHeight: 1.3 }}>
                <Num loading={loading} value={severitySummary.internalHosts.toLocaleString()} />
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#555", mt: 1.5 }}>Detected in last 7 days</Typography>
            </Box>
          </Grid>

          {/* External IPs */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ ...boxStyle }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>Internal → external IPs</Typography>
                <Box sx={ICON_BOX_SX}>
                  <PublicIcon sx={{ fontSize: 18, color: "#888" }} />
                </Box>
              </Stack>
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 22, lineHeight: 1.3 }}>
                <Num loading={loading} value={severitySummary.externalHosts.toLocaleString()} />
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#555", mt: 1.5 }}>Detected in last 7 days</Typography>
            </Box>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default SummaryCards;
